import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Link
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTermsContent } from '../hooks/useTermsContent';

/** Cap height with dvh so Safari chrome does not clip Accept. */
export const termsDialogPaperSx = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'min(90dvh, 90vh)',
  width: '100%',
  m: { xs: 1, sm: 2 },
  borderRadius: 2,
  overflow: 'hidden',
};

export const termsDialogContentSx = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
  p: 3,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
};

const TermsModal = ({ open, onClose, onAccept }) => {
  const [accepted, setAccepted] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  const { content, loading, error } = useTermsContent('user');

  const handleAccept = () => {
    if (accepted && ageVerified) {
      onAccept();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        'data-testid': 'terms-modal-paper',
        sx: termsDialogPaperSx,
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexShrink: 0,
        pb: 1
      }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          Terms of Service & Privacy Policy
        </Typography>
        <Button
          onClick={handleAccept}
          aria-label="Close"
          data-testid="terms-close-button"
          sx={{ minWidth: 'auto', p: 1 }}
          disabled={!(accepted && ageVerified)}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent
        dividers
        data-testid="terms-modal-content"
        sx={termsDialogContentSx}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" paragraph>
              Failed to load terms content. Please try again later.
            </Typography>
            <Link href="/terms-user.html" target="_blank" rel="noopener">
              View Terms in new tab
            </Link>
          </Box>
        ) : (
          <Box 
            sx={{ 
              '& h2': { 
                color: '#591716', 
                fontSize: '1.5rem',
                fontWeight: 600,
                mt: 3,
                mb: 2,
                pb: 1,
                borderBottom: '2px solid #f0f0f0'
              },
              '& h3': {
                fontSize: '1.1rem',
                fontWeight: 600,
                mt: 2,
                mb: 1
              },
              '& p': {
                mb: 2,
                lineHeight: 1.6
              },
              '& ul': {
                pl: 3,
                mb: 2
              },
              '& li': {
                mb: 1
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2
              },
              '& th': {
                bgcolor: '#591716',
                color: 'white',
                p: 1.5,
                textAlign: 'left'
              },
              '& td': {
                p: 1.5,
                borderBottom: '1px solid #ddd'
              },
              '& tr:nth-of-type(even)': {
                bgcolor: '#f8f9fa'
              },
              '& .highlight-box': {
                bgcolor: '#f8f9fa',
                borderLeft: '4px solid #591716',
                p: 2,
                my: 2,
                borderRadius: '0 8px 8px 0'
              },
              '& .warning-box': {
                bgcolor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
                p: 2,
                my: 2,
                borderRadius: '0 8px 8px 0'
              },
              '& .contact-box': {
                bgcolor: '#e8f4f8',
                p: 2,
                borderRadius: 2,
                mt: 2
              },
              '& .last-updated': {
                color: '#666',
                fontSize: '0.9rem',
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #eee'
              },
              '& a': {
                color: '#591716'
              }
            }}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}

        <Divider sx={{ my: 3 }} />

        <Box data-testid="terms-modal-consent">
          <FormControlLabel
            control={
              <Checkbox
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                <strong>Optional:</strong> I consent to receiving marketing communications, personalized 
                travel deals, and promotional offers from Déjà Vu Nomad. I can unsubscribe at any time.
              </Typography>
            }
            sx={{ mb: 1, alignItems: 'flex-start' }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={ageVerified}
                onChange={(e) => setAgeVerified(e.target.checked)}
                color="primary"
                inputProps={{ 'data-testid': 'terms-age-checkbox' }}
              />
            }
            label={
              <Typography variant="body2">
                <strong>Required:</strong> I confirm that I am at least <strong>13 years old</strong>. 
                If I am under 18, I have parental or guardian consent to use this service. 
                I understand that I must be <strong>18 or older to make purchases</strong>.
              </Typography>
            }
            sx={{ mb: 1, alignItems: 'flex-start' }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                color="primary"
                inputProps={{ 'data-testid': 'terms-accept-checkbox' }}
              />
            }
            label={
              <Typography variant="body2">
                <strong>Required:</strong> I have read and agree to the Terms of Service and Privacy Policy. 
                I understand the pricing structure, refund policy, and that my data will be processed as 
                described above.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!(accepted && ageVerified)}
          data-testid="terms-accept-button"
          sx={{ 
            minWidth: 160,
            fontWeight: 600
          }}
        >
          Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsModal;
